import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOptions,
  PlaidLinkError,
  PlaidLinkOnEvent,
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
} from 'react-plaid-link';
import { getLinkToken } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData } from 'selectors/getApplicationData';

interface UsePlaidLinkSetupProps {
  onSuccess: PlaidLinkOnSuccess;
  onExit?: (error: PlaidLinkError | null) => void;
  onEvent?: PlaidLinkOnEvent;
  skipTokenCreation?: boolean;
  skipIncomeVerification?: boolean;
}

interface UsePlaidLinkSetupReturn {
  open: Function;
  ready: boolean;
  token: string | null;
  error: Error | null;
}

export enum PlaidEvent {
  BANK_INCOME_INSIGHTS_COMPLETED = 'BANK_INCOME_INSIGHTS_COMPLETED',
  HANDOFF = 'HANDOFF',
}

export const usePlaidLinkSetup = ({
  onSuccess,
  onExit,
  onEvent,
  skipTokenCreation = false,
  skipIncomeVerification,
}: UsePlaidLinkSetupProps): UsePlaidLinkSetupReturn => {
  const { id: applicationId } = useSelector(getApplicationData)?.application ?? {};
  const [token, setToken] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const defaultOnExit = useCallback(
    (exitError: PlaidLinkError | null) => {
      analytics.track('Plaid Link Closed', {
        ...(exitError && { error: exitError }),
      });
      onExit?.(exitError);
    },
    [onExit],
  );

  const defaultOnEvent = useCallback<PlaidLinkOnEvent>(
    (evtName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
      analytics.track(`Plaid Event: ${evtName}`, {
        metadata,
      });
      onEvent?.(evtName, metadata);
    },
    [onEvent],
  );

  useEffect(() => {
    if (skipTokenCreation || !applicationId || token) return;

    const createLinkToken = async () => {
      try {
        if (isLoadingToken) return;

        setIsLoadingToken(true);
        setError(null);

        const response = await dispatchWithUnwrap(
          getLinkToken({
            applicationId,
            skipIncomeVerification,
          }),
        );
        const { LINK_TOKEN: linkToken } = response;

        setToken(linkToken);
        setIsLoadingToken(false);
      } catch (tokenError) {
        setError(tokenError instanceof Error ? tokenError : new Error('Failed to create Plaid link token'));
        setIsLoadingToken(false);
      }
    };

    createLinkToken();
  }, [applicationId, dispatchWithUnwrap, skipTokenCreation, skipIncomeVerification]);

  const config: PlaidLinkOptions = {
    token,
    onSuccess,
    onExit: defaultOnExit,
    onEvent: defaultOnEvent,
  };

  const { open, ready } = usePlaidLink(config);

  return {
    open,
    ready,
    token,
    error,
  };
};
